import {
  deserify,
  FilterSortPopup,
  ImageWrapper,
  pxToRem,
  pxTovW,
  SearchBar,
  useCommonServiceClientContext,
} from '@geneo2-web/shared-ui';
import ProjectInfoCardWithGroups from '../../../components/ProjectInfoCardWithGroups';
import { Box, Button, Grid, Typography } from '@mui/material';
import {
  useAppDispatch,
  useAppSelector,
  useDeserifiedSelector,
} from 'apps/teacher/src/reduxStore/reduxHooks';
import { useEffect, useMemo, useState } from 'react';
import { TabComp } from 'apps/teacher/src/components/TabComp';
import {
  AssessmentEnum,
  ModeOfConductionEnum,
} from '@protos/learning_management/lms.db_pb';
import { AssessmentTask } from '@protos/learning_management/lms.assessment.common.apis_pb';
import { setSelectedProjectId } from '../CreateProject/reducer/projectFlow.Slice';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  GIL_Reports,
  HOMEWORK_MANAGE,
  View_Project,
} from '../../../routeHandling/RoutesNomenclature';
import { useGlobalContext } from 'apps/teacher/src/app/Context/GlobalContextProvider';
import { ShimmerProjectCard } from 'libs/shared-ui/src/components/composites/ShimmerCards';
import { styles } from './styles';
import { TeacherLoginResponseType } from '@protos/user_management/ums.login.apis_pb';
import ProjectDashboardBanner from '../components/ProjectDashboardBanner';
import ClassAndSubjectPicker from '../components/ClassAndSubjectPicker';

interface IProjects {
  Assigned: AssessmentTask[];
  Ended: AssessmentTask[];
}

const SORT_OPTIONS = [
  'Name: A to Z',
  'Name: Z to A',
  'Newest First',
  'Oldest First',
  'Deadline',
];

/** Safety cap on pages fetched per load (24 per page => ~1200 projects). */
const MAX_PAGES = 50;

const toMillis =(ts?: { seconds?: bigint | number }) =>
  ts?.seconds !== undefined ? Number(ts.seconds) * 1000 : 0;

const sortProjects = (list: AssessmentTask[], sortBy: string) => {
  const sorted = [...list];
  switch (sortBy) {
    case 'Name: A to Z':
      return sorted.sort((a, b) =>
        (a.assessmentTitle ?? '').localeCompare(b.assessmentTitle ?? '')
      );
    case 'Name: Z to A':
      return sorted.sort((a, b) =>
        (b.assessmentTitle ?? '').localeCompare(a.assessmentTitle ?? '')
      );
    case 'Newest First':
      return sorted.sort(
        (a, b) =>
          toMillis(b.assessmentAssignedDate) -
          toMillis(a.assessmentAssignedDate)
      );
    case 'Oldest First':
      return sorted.sort(
        (a, b) =>
          toMillis(a.assessmentAssignedDate) -
          toMillis(b.assessmentAssignedDate)
      );
    case 'Deadline':
      return sorted.sort(
        (a, b) =>
          toMillis(a.assessmentTargetDate) - toMillis(b.assessmentTargetDate)
      );
    default:
      return list;
  }
};

const ProjectDashboard = () => {
  const tabs = ['Assigned', 'Ended'];

  // Teacher profile
  const userInfo = deserify(useAppSelector((state) => state.auth))
    ?.user_info as TeacherLoginResponseType;
  const teacherProfileId = userInfo?.teacherProfileId?.toString();

  const class_subject_info = useDeserifiedSelector(
    (state) => state.homeDashboard.class_subject_info
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { setSelectedFunction } = useGlobalContext();

  // Going back from here would land on the congratulations page of a project
  // that is already assigned, so send the teacher to the assessment page instead.
  useEffect(() => {
    if (!location.state?.fromProjectCongratulations) return;
    setSelectedFunction(() => () => navigate(HOMEWORK_MANAGE));
    return () => {
      setSelectedFunction(null);
    };
  }, []);

  /** States */
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<any>(null);

  // The home page's "See All" says which section it came from.
  const [activeTab, setActiveTab] = useState<string>(
    tabs.includes(location.state?.assessmentState)
      ? location.state.assessmentState
      : tabs[0]
  );
  const [projects, setProjects] = useState<IProjects>({
    Assigned: [],
    Ended: [],
  });
  const [searchText, setSearchText] = useState('');
  const [sortBy, setSortBy] = useState('');

  const visibleProjects = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    const filtered = projects[activeTab as keyof IProjects].filter(
      (project) =>
        !query || project.assessmentTitle?.toLowerCase().includes(query)
    );
    return sortProjects(filtered, sortBy);
  }, [projects, activeTab, searchText, sortBy]);

  const { LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler } =
    useCommonServiceClientContext();

  const handleTabChange = async (tab: string) => {
    setActiveTab(tab);
  };

  // The API pages results (countInPagination per page, both tabs share the
  // page number). Search and sort run client-side, so load every page up front
  // - otherwise projects past the first page are invisible and unsearchable.
  const getTeacherAssessmentList = async (
    teacherId: string,
    isStale: () => boolean
  ) => {
    const fetchPage = (pageNumber: number) =>
      LmsAssessmentTeacherAPIServiceV1WithStatusCodeHandler.getTeacherAssessmentsList(
        {
          teacherId: BigInt(teacherId),
          subjectId: class_subject_info?.subjectId,
          sectionId: class_subject_info?.sectionId,
          pageNumber,
          assessmentType: AssessmentEnum.ASSESSMENT_PROJECT,
          modeOfConduction: ModeOfConductionEnum.MODE_OF_CONDUCTION_UNDEFINED,
        }
      ).then((response) => response.assessmentList?.assessmentList);

    try {
      setError(null);
      setLoading(true);
      const firstPage = await fetchPage(1);
      const info = firstPage?.assesmentListInfo;
      const perPage = info?.countInPagination || 0;
      const totalPages = perPage
        ? Math.min(
            Math.ceil(
              Math.max(info?.activeCount ?? 0, info?.endedCount ?? 0) / perPage
            ),
            MAX_PAGES
          )
        : 1;
      const restPages = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2))
      );
      if (isStale()) return;

      const pages = [firstPage, ...restPages];
      setProjects({
        Assigned: pages.flatMap((page) => page?.assigned ?? []),
        Ended: pages.flatMap((page) => page?.ended ?? []),
      });
    } catch (err) {
      if (isStale()) return;
      setError(err);
      console.log(err);
    } finally {
      if (!isStale()) setLoading(false);
    }
  };

  useEffect(() => {
    let stale = false;
    if (teacherProfileId) {
      getTeacherAssessmentList(teacherProfileId, () => stale);
    }
    return () => {
      stale = true;
    };
  }, [
    class_subject_info?.classId,
    class_subject_info?.sectionId,
    class_subject_info?.subjectId,
  ]);

  return (
    <Box sx={styles.root}>
      <ProjectDashboardBanner />
      <Box sx={styles.reportsButtonBox}>
        <Button
          onClick={() =>
            navigate(
              class_subject_info?.sectionId
                ? `${GIL_Reports}?sectionId=${class_subject_info.sectionId}`
                : GIL_Reports
            )
          }
          sx={styles.reportsButton}
        >
          <ImageWrapper
            name="reports"
            type="png"
            parentFolder="icons"
            styles={styles.reportsButtonIcon}
          />
          <Typography variant="h4" fontWeight="bold" color="common.white">
            GIL Reports
          </Typography>
        </Button>
      </Box>

      <TabComp
        handleChange={handleTabChange}
        mapData={tabs}
        stateValue={activeTab}
        count={visibleProjects.length}
      />

      <Box sx={styles.inputFieldBox}>
        <Box sx={styles.inputBox}>
          <ClassAndSubjectPicker
            // Refetch is driven by class_subject_info changing (see effect).
            onClassSubjectClick={() => undefined}
            interactionEventContext={activeTab}
            teacherClassSubjects={userInfo?.teachClassSubjects}
          />

          <Box sx={styles.searchBox}>
            <SearchBar
              searchText={searchText}
              handleSearchTextChange={setSearchText}
              handleSearch={(text) => setSearchText(text ?? '')}
              handleClose={() => setSearchText('')}
            />
          </Box>
          <Box sx={styles.sortBox}>
            <FilterSortPopup
              iconName="Sort"
              title="Sort By"
              options={SORT_OPTIONS}
              sortFunction={setSortBy}
            />
          </Box>
          {/* <Box sx={{
                        marginY: { md: 'auto' },
                        marginX: { xs: 'auto', md: 0 },
                    }}>
                        <FilterSortPopup
                            iconName="Filter"
                            title="Filter"
                            options={[]}
                        />
                    </Box> */}
          {/* <Box sx={{
                        marginY: { md: 'auto' },
                        marginX: { xs: 'auto', md: 0 },
                    }}>
                        <Button
                            variant="contained"
                            onClick={() => setProjectSelectionOpen(true)}
                            sx={{
                                width: {
                                    xs: '100%',
                                    md: pxTovW(206),
                                },
                                height: { xs: pxToRem(52), lg: pxTovW(56) },
                                fontFamily: 'Lato',
                                fontWeight: 700,
                                fontSize: { xs: '18px', md: pxToRem(17), lg: pxTovW(18) },
                                // boxShadow:
                                //     'inset 0 0 8px rgba(0, 0, 0, 0.5),0 7px 13px rgba(0, 0, 0, 0.5)',
                                borderRadius: '10px',
                                backgroundColor: '#475D99',
                            }}
                        >Teach New Project +</Button>
                    </Box> */}
        </Box>
      </Box>

      <Box
        sx={{
          paddingTop: { xs: pxToRem(20), md: pxToRem(30), lg: pxTovW(40) },
          paddingBottom: { xs: pxToRem(20), md: pxToRem(30), lg: pxTovW(40) },
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: pxToRem(20), md: pxToRem(18), lg: pxTovW(20) },
            width: { xs: '100%', md: pxTovW(1800), lg: pxTovW(1441) },
          }}
        >
          {loading && (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <ShimmerComponent />
            </Box>
          )}

          {!loading && !!error && <Typography>Error Occurred</Typography>}

          {!loading && !error && (
            <Grid
              container
              rowGap={3}
              sx={{
                width: { xs: '100%', md: pxTovW(1800), lg: pxTovW(1441) },
              }}
            >
              {visibleProjects.length === 0 && (
                <Typography
                  variant="h4"
                  sx={{ width: '100%', textAlign: 'center' }}
                >
                  {searchText.trim()
                    ? `No projects found for "${searchText.trim()}"`
                    : 'No projects found'}
                </Typography>
              )}
              {visibleProjects.map((card, index) => (
                <Grid
                  item
                  xs={12}
                  sm={12}
                  md={4}
                  key={String(card.assessmentId ?? index)}
                >
                  <ProjectInfoCardWithGroups
                    project={card}
                    rootStyles={{ maxWidth: '100%' }}
                    onClick={() => {
                      navigate(`${View_Project}/${card.assessmentId}`);
                      dispatch(setSelectedProjectId(card?.assessmentId));
                    }}
                  />
                </Grid>
              ))}
            </Grid>
          )}
        </Box>
      </Box>

      {/* DOUBT::: Is this required now? */}
      {/* <ProjectSelectionPopup
                popupOpen={projectSelectionOpen}
                popupCloseHandler={() => setProjectSelectionOpen(false)}
                popupTitle="Create New Project"
                chooseClassData={{
                    selectedClass,
                    classList,
                    handleClassChange: (value) => setSelectedClass(value),
                }}
                chooseSectionData={{
                    selectedSection,
                    sectionList,
                    handleSectionChange: (value) => setSelectedSection(value),
                }}
                chooseSubjectData={{
                    selectedSubject,
                    subjectList,
                    handleSubjectChange: (value) => setSelectedSubject(value),
                }}
                nameChangeData={{
                    title: 'Project Name',
                    name: 'projectName',
                    value: projectName,
                    onChange: (e) => setProjectName(e.target.value),
                }}
                sectionInputNode={null}
                addMoreSectionData={{
                    handleAddMore: () => { },
                    idAddMoreDisabled: true,
                }}
                proceedButtonData={{
                    onClick: () => {
                        console.log({
                            selectedClass,
                            selectedSection,
                            selectedSubject,
                            projectName,
                        });

                        setProjectSelectionOpen(false);
                    },
                    disabled: !projectName.trim(),
                }}
            /> */}
    </Box>
  );
};

export default ProjectDashboard;

export const ShimmerComponent = () => {
  return (
    <Grid container rowGap={3} sx={{ width: { md: pxTovW(1400) } }}>
      {Array.from({ length: 12 }, (_, index) => (
        <Grid item xs={12} md={4} key={index}>
          <ShimmerProjectCard key={index} />
        </Grid>
      ))}
    </Grid>
  );
};
